/**
 * LeadFunnelActions — barra de 6 botones para transicionar leads por el funnel.
 *
 * Cada botón invoca la RPC `transition_lead` que hace atómicamente:
 *   - UPDATE leads.status (con reglas de anti-retroceso)
 *   - INSERT en activities con type + metadata + owner
 *
 * Los inputs se piden en un modal custom (antes eran window.prompt/alert
 * bloqueantes que no funcionaban en móvil ni tenían validación decente).
 *
 * Referencias: /tmp/spec-p0-final.md § 4 (Comportamiento de los botones)
 */

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import type { Lead } from '../types';
import type { LeadStatusKey } from '../constants/leadStatuses';

interface LeadFunnelActionsProps {
  leadId: string;
  currentStatus: LeadStatusKey | Lead['status'];
  /** Owner por defecto para las activities. Si no se pasa se usa el email de auth.getUser() o 'system:crm'. */
  defaultActor?: string;
  onTransition?: (result: { newStatus: LeadStatusKey; activityId: string; metadata: Record<string, any> }) => void;
}

type CanalContacto = 'email' | 'llamada' | 'whatsapp' | 'linkedin' | 'in_person';

const CANAL_OPTIONS: { value: CanalContacto; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'llamada', label: 'Llamada' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'in_person', label: 'Presencial' },
];

const LOST_REASON_OPTIONS = [
  { value: 'cliente_eligio_otro', label: 'Cliente eligió otro proveedor' },
  { value: 'sin_presupuesto', label: 'Sin presupuesto' },
  { value: 'no_encaje', label: 'No encaja el producto/servicio' },
  { value: 'silencio', label: 'Silencio del cliente' },
  { value: 'plazo', label: 'Plazo no encaja' },
  { value: 'otro', label: 'Otro' },
];

type ActionKind = 'contactado' | 'respondio' | 'cualificado' | 'propuesta' | 'ganado' | 'perdido';

interface ActionSpec {
  kind: ActionKind;
  label: string;
  buttonClass: string;
  newStatus: LeadStatusKey;
  activityType: string;
}

const ACTION_SPECS: Record<ActionKind, ActionSpec> = {
  contactado:  { kind: 'contactado',  label: 'Contactado',   buttonClass: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',   newStatus: 'contactado' as LeadStatusKey,       activityType: 'contact_attempt' },
  respondio:   { kind: 'respondio',   label: 'Respondió',    buttonClass: 'bg-amber-100 text-amber-800 hover:bg-amber-200',       newStatus: 'en_seguimiento' as LeadStatusKey,   activityType: 'replied' },
  cualificado: { kind: 'cualificado', label: 'Cualificado',  buttonClass: 'bg-green-100 text-green-800 hover:bg-green-200',       newStatus: 'calificado' as LeadStatusKey,       activityType: 'qualified' },
  propuesta:   { kind: 'propuesta',   label: 'Propuesta €',  buttonClass: 'bg-purple-100 text-purple-800 hover:bg-purple-200',   newStatus: 'propuesta' as LeadStatusKey,         activityType: 'proposal_sent' },
  ganado:      { kind: 'ganado',      label: 'Ganado €',     buttonClass: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200', newStatus: 'ganado' as LeadStatusKey,           activityType: 'won' },
  perdido:     { kind: 'perdido',     label: 'Perdido',      buttonClass: 'bg-red-100 text-red-800 hover:bg-red-200',             newStatus: 'perdido' as LeadStatusKey,          activityType: 'lost' },
};

async function callTransitionLead(
  leadId: string,
  newStatus: LeadStatusKey | null,
  activityType: string,
  metadata: Record<string, any>,
  actor: string,
) {
  const { data, error } = await supabase.rpc('transition_lead', {
    p_lead_id: leadId,
    p_new_status: newStatus,
    p_activity_type: activityType,
    p_metadata: metadata,
    p_actor: actor,
  });
  if (error) throw error;
  return data;
}

const LeadFunnelActions: React.FC<LeadFunnelActionsProps> = ({
  leadId,
  currentStatus,
  defaultActor,
  onTransition,
}) => {
  const [actor, setActor] = useState<string>(defaultActor || 'system:crm');
  const [loading, setLoading] = useState<ActionKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<ActionKind | null>(null);

  useEffect(() => {
    if (defaultActor) return;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) setActor(user.email);
      } catch {
        // Silencioso: dejamos 'system:crm' como fallback.
      }
    })();
  }, [defaultActor]);

  const isTerminal = ['ganado', 'perdido', 'archivado'].includes(currentStatus);

  async function submitAction(spec: ActionSpec, metadata: Record<string, any>) {
    setError(null);
    setLoading(spec.kind);
    setOpenDialog(null);
    try {
      const activity = await callTransitionLead(leadId, spec.newStatus, spec.activityType, metadata, actor);

      // Perdido: además persistir lost_reason en la columna dedicada de leads.
      if (spec.kind === 'perdido' && metadata.reason) {
        await supabase.from('leads').update({ lost_reason: metadata.reason }).eq('id', leadId);
      }

      if (onTransition) {
        onTransition({
          newStatus: spec.newStatus,
          activityId: (activity as any)?.id ?? '',
          metadata,
        });
      }
    } catch (e: any) {
      console.error(`[LeadFunnel] Error en ${spec.label}:`, e);
      setError(e?.message ?? `Error al ejecutar ${spec.label}`);
    } finally {
      setLoading(null);
    }
  }

  const btnBase =
    'px-3 py-1.5 text-xs font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="border rounded-lg bg-gray-50 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Funnel · acciones rápidas
        </h4>
        {isTerminal && (
          <span className="text-xs text-gray-500">
            Lead cerrado ({currentStatus}) — reversión bloqueada
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.values(ACTION_SPECS).map(spec => (
          <button
            key={spec.kind}
            type="button"
            disabled={loading !== null || isTerminal}
            onClick={() => setOpenDialog(spec.kind)}
            className={`${btnBase} ${spec.buttonClass}`}
          >
            {loading === spec.kind ? '…' : spec.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          ⚠ {error}
        </div>
      )}

      <p className="text-xs text-gray-500 leading-snug">
        Cada botón registra el evento en <code className="text-[10px]">activities</code> con metadata
        y actualiza <code className="text-[10px]">leads.status</code> vía RPC transaccional.
        No retrocede estados. Los estados terminales (ganado/perdido/archivado) bloquean cambios.
      </p>

      {openDialog && (
        <FunnelDialog
          spec={ACTION_SPECS[openDialog]}
          onClose={() => setOpenDialog(null)}
          onSubmit={(metadata) => submitAction(ACTION_SPECS[openDialog], metadata)}
        />
      )}
    </div>
  );
};

// ── Modal de captura de metadata por acción ───────────────────────

interface FunnelDialogProps {
  spec: ActionSpec;
  onClose: () => void;
  onSubmit: (metadata: Record<string, any>) => void;
}

const FunnelDialog: React.FC<FunnelDialogProps> = ({ spec, onClose, onSubmit }) => {
  const [channel, setChannel] = useState<CanalContacto>('email');
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('silencio');
  const [reasonText, setReasonText] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);

    if (spec.kind === 'contactado' || spec.kind === 'respondio') {
      onSubmit({ channel });
      return;
    }

    if (spec.kind === 'cualificado') {
      const value = amount.trim() === '' ? null : parseInt(amount.replace(/[^\d]/g, ''), 10);
      onSubmit({ estimated_value_eur: Number.isFinite(value) ? value : null, source: 'manual' });
      return;
    }

    if (spec.kind === 'propuesta' || spec.kind === 'ganado') {
      const value = parseInt(amount.replace(/[^\d]/g, ''), 10);
      if (!Number.isFinite(value) || value <= 0) {
        setLocalError('Introduce un importe válido en euros (> 0).');
        return;
      }
      const key = spec.kind === 'propuesta' ? 'amount_eur' : 'closed_value_eur';
      onSubmit({ [key]: value, source: 'manual' });
      return;
    }

    if (spec.kind === 'perdido') {
      const finalReason = reason === 'otro' && reasonText.trim() ? reasonText.trim() : reason;
      onSubmit({ reason: finalReason, source: 'manual' });
      return;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">{spec.label}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {(spec.kind === 'contactado' || spec.kind === 'respondio') && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Canal
            </label>
            <select
              value={channel}
              onChange={e => setChannel(e.target.value as CanalContacto)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {CANAL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {(spec.kind === 'cualificado' || spec.kind === 'propuesta' || spec.kind === 'ganado') && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {spec.kind === 'cualificado' && 'Importe estimado en € (opcional)'}
              {spec.kind === 'propuesta' && 'Importe de la propuesta en €'}
              {spec.kind === 'ganado' && 'Importe cerrado en €'}
            </label>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
        )}

        {spec.kind === 'perdido' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Motivo
              </label>
              <select
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {LOST_REASON_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {reason === 'otro' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Detalle (opcional)
                </label>
                <input
                  type="text"
                  value={reasonText}
                  onChange={e => setReasonText(e.target.value)}
                  placeholder="Ej: sin decisor identificado"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </>
        )}

        {localError && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            {localError}
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Confirmar
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadFunnelActions;
